/**
 * @file This file is the root component of the application.
 * It sets up the main routing structure using React Router and includes
 * providers for session management and team context. It also handles
 * lazy loading of components to improve initial load times.
 */

import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Outlet } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import SessionManager from './components/SessionManager';
import { TeamProvider } from './context/TeamContext';
import Footer from './components/common/Footer';
import ScrollToTop from './components/common/ScrollToTop'; // Import the new component
const GuestHomePage = lazy(() => import('./components/GuestHomePage'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Login = lazy(() => import('./components/Login'));
const Signup = lazy(() => import('./components/Signup'));
const MySettings = lazy(() => import('./components/MySettings'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const ProtectedRoute = lazy(() => import('./components/auth/ProtectedRoute'));
const NotFoundPage = lazy(() => import('./components/common/NotFoundPage'));
const PrivacyPolicy = lazy(() => import('./components/common/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/common/TermsOfService'));
const SEOTest = lazy(() => import('./components/SEOTest'));

/**
 * The main application component.
 * It sets up the router, routes, and global components like the footer.
 *
 * @returns {JSX.Element} The rendered application component.
 */
function App() {
  // We no longer need these memoized components since we're using them directly in the Routes

  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            {/* Public routes (no session required) */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/seo-test" element={<SEOTest />} />

            {/* Authenticated routes using a layout */}
            <Route element={
              <SessionManager>
                <TeamProvider>
                  <Outlet /> {/* Renders child routes */}
                </TeamProvider>
              </SessionManager>
            }>
              <Route path="/" element={<GuestHomePage />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/my-settings" element={<MySettings />} />
              <Route path="/admin" element={
                <ProtectedRoute requiredAdmin={true}>
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="*" element={<NotFoundPage />} /> {/* Catch-all for authenticated but not found */}
            </Route>
          </Routes>
          <Footer /> {/* Global footer */}
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
